import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  type CountryCode,
} from "libphonenumber-js";

// Starting prefix: the region of the guest's first browser locale that
// carries one (en-US -> US), Italy otherwise. A foreign guest who never
// opens the list still lands on their own prefix.
export function defaultPhoneCountry(
  locales: readonly string[] = typeof navigator !== "undefined"
    ? navigator.languages ?? [navigator.language]
    : []
): CountryCode {
  for (const locale of locales) {
    const region = locale?.split(/[-_]/)[1]?.toUpperCase();
    if (region && isSupportedCountry(region)) return region as CountryCode;
  }
  return "IT";
}

interface Country {
  code: CountryCode;
  name: string;
  dial: string;
  flag: string;
}

// ISO 3166 alpha-2 -> regional-indicator flag emoji (no image assets needed).
function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

interface PhoneInputProps {
  country: CountryCode;
  onCountryChange: (c: CountryCode) => void;
  value: string;
  onChange: (v: string) => void;
  language: string;
  placeholder: string;
  searchPlaceholder: string;
  invalid?: boolean;
  inputId?: string;
}

export default function PhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  language,
  placeholder,
  searchPlaceholder,
  invalid,
  inputId,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  const countries = useMemo<Country[]>(() => {
    let names: { of: (c: string) => string | undefined } | null = null;
    try {
      // Localised country names with zero extra payload.
      names = new (Intl as any).DisplayNames([language || "en"], {
        type: "region",
      });
    } catch {
      names = null;
    }
    return getCountries()
      .map((code) => ({
        code,
        name: names?.of(code) ?? code,
        dial: "+" + getCountryCallingCode(code),
        flag: flagEmoji(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [language]);

  const selected =
    countries.find((c) => c.code === country) ??
    countries.find((c) => c.code === "IT") ??
    countries[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q.replace(/^\+?/, "+")) ||
        c.dial.includes(q) ||
        c.code.toLowerCase() === q
    );
  }, [countries, query]);

  // Close the dropdown on outside click. On customer sites the widget lives in
  // a shadow root, where a document listener sees e.target retargeted to the
  // shadow host: contains(e.target) was always false, so every press inside
  // the list closed it before the click landed and the prefix stuck on Italy.
  // composedPath() still lists the real nodes inside an open shadow root.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !e.composedPath().includes(wrapRef.current)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = (c: Country) => {
    onCountryChange(c.code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      className={`handoff-phone${invalid ? " handoff-phone--invalid" : ""}`}
      ref={wrapRef}
    >
      <button
        type="button"
        className="handoff-phone-prefix"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `${selected.name} ${selected.dial}` : "prefix"}
      >
        <span className="handoff-phone-flag">{selected?.flag}</span>
        <span className="handoff-phone-dial">{selected?.dial}</span>
        <i className="fa-solid fa-chevron-down handoff-phone-caret"></i>
      </button>

      <input
        id={inputId}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className="handoff-phone-number"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />

      {open && (
        <div className="handoff-phone-dropdown" role="listbox">
          <input
            className="handoff-phone-search"
            type="text"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <ul className="handoff-phone-list">
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  className={`handoff-phone-option${
                    c.code === country ? " is-selected" : ""
                  }`}
                  onClick={() => choose(c)}
                >
                  <span className="handoff-phone-flag">{c.flag}</span>
                  <span className="handoff-phone-option-name">{c.name}</span>
                  <span className="handoff-phone-option-dial">{c.dial}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="handoff-phone-empty">—</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
